import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { request } from "utils/request";

import {
  ItemsDetailResponse,
  ItemsForm,
  ItemsListResponse,
  ItemsUpdate,
} from "../types";
import { selectItemsForm } from "./selector";
import { itemsActions } from "./slice";

let isFetchingList = false;

export function* handleGetItemsList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const response: ItemsListResponse = yield call(
      request,
      `${API_URL}/item?page=1&perPage=20`,
      { method: "GET" },
    );

    yield put(itemsActions.getItemsListSuccess(response?.data || []));
  } catch (error) {
    yield put(itemsActions.getItemsListError((error as Error).message));
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddItem() {
  const form: ItemsForm = yield select(selectItemsForm);

  try {
    yield call(request, `${API_URL}/item`, {
      method: "POST",
      body: JSON.stringify({
        itemName: form.item.trim(),
        itemDesc: form.detailedDescription.trim(),
        height: Number(form.height),
        width: Number(form.width),
        thick: Number(form.thickness),
        length: Number(form.length),
        weight: Number(form.weight),
        itemType: form.group.trim(),
        remarks: form.notes.trim(),
      }),
    });

    yield put(itemsActions.addItemSuccess());
    yield put(itemsActions.getItemsList());
  } catch (error) {
    yield put(itemsActions.addItemError((error as Error).message));
  }
}

export function* handleUpdateItem(action: PayloadAction<ItemsUpdate>) {
  const {
    id,
    itemName,
    itemDesc,
    height,
    width,
    thick,
    length,
    weight,
    itemType,
    remarks,
  } = action.payload;

  try {
    yield call(request, `${API_URL}/item/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        itemName: itemName.trim(),
        itemDesc: itemDesc.trim(),
        height: Number(height),
        width: Number(width),
        thick: Number(thick),
        length: Number(length),
        weight: Number(weight),
        itemType: itemType.trim(),
        remarks: remarks.trim(),
      }),
    });

    yield put(itemsActions.updateItemSuccess());
    yield put(itemsActions.getItemsList());
  } catch (error) {
    yield put(itemsActions.updateItemError((error as Error).message));
  }
}

export function* handleGetItemDetail(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    const response: ItemsDetailResponse = yield call(
      request,
      `${API_URL}/item/${id}`,
      { method: "GET" },
    );

    yield put(itemsActions.getItemDetailSuccess(response?.data));
  } catch (error) {
    yield put(itemsActions.getItemDetailError((error as Error).message));
  }
}

export function* handleDeleteItem(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/item/${id}`, {
      method: "DELETE",
    });

    yield put(itemsActions.deleteItemSuccess(id));
  } catch (error) {
    yield put(itemsActions.deleteItemError((error as Error).message));
  }
}

export function* itemsSaga() {
  yield takeLeading(itemsActions.getItemsList.type, handleGetItemsList);
  yield takeLatest(itemsActions.addItem.type, handleAddItem);
  yield takeLatest(itemsActions.updateItem.type, handleUpdateItem);
  yield takeLatest(itemsActions.getItemDetail.type, handleGetItemDetail);
  yield takeLatest(itemsActions.deleteItem.type, handleDeleteItem);
}

export default itemsSaga;
